import { noop } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Card, CardHeader, Col, Row } from "reactstrap";
import Button from "../../../components/Button";
import Drawer from "../../../components/Drawer";
import NTable from "../../../components/NTable";
import Actions from "../../../components/NTable/Actions";
import { useNewTable } from "../../../components/NTable/hook";
import { formatParams } from "../../../utils/api/format";
import { useInitialState } from "../../../utils/hooks/useInitialState";
import { useVisible } from "../../../utils/hooks/useVisible";
import { useAuthentication } from "../../Authentication/context";
import { useGroups } from "../../Settings/General/OrganisationLevelGroups/context";
import { useDeleteModal } from "../../Settings/helpers/useDeleteModal";
import { useQueryFilters } from "../../Settings/helpers/useQueryFilters";
import PlansTable from "../../TimeOff/Home/PlansTable";
import AddAbsence from "../AddAbsence";
import { useWarningModal } from "../AddAbsence/WarningModal";
import AddPayCode from "../AddPayCode";
import AddSchedule from "../AddSchedule";
import AddShift from "../AddShift";
import { useMyRequests } from "../api";
import { useViewDrawer } from "../Team/hooks/useViewDrawer";
import BulkActions from "./BulkActions";
import { useColumns } from "./columns";
import { useCancelModal } from "./hooks/useCancelModal";
import { useEditDrawer } from "./hooks/useEditDrawer";
import "./styles.scss";
import { buildRequestTypeFilter, canRequest, resolveApiNamespace } from "../permissions";

function MyRequest({
	timeoff = false,
	namespace,
	filterType,
	hasChanges,
	setHasChanges = noop,
}) {
	const { t } = useTranslation();
	const { accessMap } = useAuthentication();
	const { groups } = useGroups();
	const { visible, selected: addRequestType, open, close } = useVisible();
	const [searchParams] = useSearchParams();
	const [canRefetchPlans, setCanRefetchPlans] = useState(false);

	const {
		data,
		setData,
		totalItems,
		setTotalItems,
		loading,
		fetch,
		deleteRequest,
		batchRemove,
		update,
		error,
		cancelRequest,
	} = useMyRequests();

	const { open: openCancelModal, modal: cancelModal } = useCancelModal({
		cancel: cancelRequest,
		setData,
	});

	const { modal: deleteModal, open: openDeleteModal } = useDeleteModal({
		message: (data) =>
			Array.isArray(data)
				? t("do-you-want-to-delete-requests", {
					count: data.length,
				})
				: t("do-you-want-to-delete-request"),
		onConfirm: (data) =>
			Array.isArray(data)
				? batchRemove(data, () => {
					setData((prev) => prev?.filter((item) => !data.includes(item?.id)));
					setTotalItems((prev) => prev - data.length);
					setCanRefetchPlans(true);
					toast.success(t("request-deleted-successfully"));
				})
				: deleteRequest(data, () => {
					setData((prev) => prev?.filter((item) => item?.id !== data));
					setTotalItems((prev) => prev - 1);
					setCanRefetchPlans(true);
					toast.success(t("request-deleted-successfully"));
				}),
	});

	const { open: openWarningModal, warningModal } = useWarningModal({
		closeDrawer: () => { },
		setData,
		setTotalItems,
		setCanRefetchPlans,
	});

	const {
		visible: isEditOpen,
		open: openEdit,
		close: closeEdit,
		content: editDrawerContent,
	} = useEditDrawer({
		update,
		error,
		loading,
		setData,
		openWarningModal,
		setCanRefetchPlans,
	});

	const { open: openViewDrawer, drawer } = useViewDrawer({
		timeoff: timeoff || namespace === "pto",
	});

	const onAddPayCodeSuccess = useCallback(
		(request) => {
			setData((prev) => {
				prev = Array.isArray(prev) ? prev : [];
				return [...prev, request];
			});
			setTotalItems((prev) => (prev || 0) + 1);
		},
		[setData, setTotalItems],
	);

	const columns = useColumns({
		view: (row) =>
			canRequest(accessMap, { namespace, type: row?.type, action: "canView" })
				? () => openViewDrawer(row)
				: null,
		edit: (row) =>
			canRequest(accessMap, { namespace, type: row?.type, action: "canEdit" })
				? () => openEdit(row)
				: null,
		remove: (row) =>
			canRequest(accessMap, { namespace, type: row?.type, action: "canDelete" })
				? () => openDeleteModal(row.id)
				: null,
		cancel: (row) =>
			canRequest(accessMap, { namespace, type: row?.type, action: "canCancel" })
				? () => openCancelModal(row)
				: null,
		timeoff: timeoff || namespace === "pto",
		namespace,
	});

	const sessionFiltersKey = useMemo(() => {
		return !timeoff ? `my${namespace}Requests` : "myTimeoff";
	}, [timeoff, namespace]);

	const table = useNewTable({
		data,
		columns,
		totalItems,
		loading,
		initialState: useInitialState(() => {
			const columnVisibility = groups
				.filter(({ allocatedBy }) => !allocatedBy)
				.reduce((total, group) => {
					const level = group.level.replace("_", "");
					return {
						...total,
						[level]: false,
					};
				}, {});
			return { columnVisibility };
		}),
		sessionFiltersKey,
	});
	const queryFilters = useQueryFilters(table);

	useEffect(() => {
		const controller = new AbortController();
		const filters = {
			namespace: resolveApiNamespace(namespace),
			...queryFilters,
			filters: [
				...queryFilters.filters.filter((filter) => filter.name !== "type"),
				filterType !== "all"
					? {
						name: "type",
						method: "exact",
						value: filterType,
					}
					: {},
				buildRequestTypeFilter(namespace, filterType),
			],
		};
		fetch(formatParams(filters), controller);
		return () => controller.abort();
	}, [fetch, queryFilters, filterType, namespace]);

	useEffect(() => {
		const id = searchParams.get("id");
		const type = searchParams.get("type");
		if (id) {
			openViewDrawer({ id, type });
		}
	}, [openViewDrawer, searchParams]);

	useEffect(() => {
		setHasChanges(true);
	}, [namespace, setHasChanges]);

	return (
		<div className="d-flex flex-column h-100">
			<Card
				className="flex-fill mb-0"
				style={{ maxHeight: "calc(100% - 300px)" }}
			>
				{namespace === "pto" && (
					<div className="m-4">
						<PlansTable />
					</div>
				)}
				<CardHeader className="border-0 rounded-0 mb-0 pb-1">
					<Row>
						{/* <Col sm={12} md={6}>
							<h3>{t("request")}</h3>
						</Col> */}

						<Col sm={12} md={12} className="text-md-right">
							{namespace === "clock" &&
								canRequest(accessMap, {
									namespace: "clock",
									action: "canCreate",
								}) && (
									<Button
										onClick={() => open("add-shift")}
										className="border-primary px-3 btn-round mb-2 mb-md-2 btn btn-secondary btn-sm text-primary"
									>
										{t("add-shift-request")}
									</Button>
								)}

							{(namespace === "pto" || timeoff) &&
								canRequest(accessMap, {
									namespace: "pto",
									action: "canCreate",
								}) && (
									<Button
										onClick={() => open("add-absence")}
										className="border-primary px-3 btn-round mb-2 mb-md-2 btn btn-secondary btn-sm text-primary ml-0"
									>
										{t("add-absence-request")}
									</Button>
								)}

							{namespace === "schedule" &&
								canRequest(accessMap, {
									namespace: "schedule",
									action: "canCreate",
								}) && (
									<Button
										onClick={() => open("add-schedule")}
										className="border-primary px-3 btn-round mb-2 mb-md-2 btn btn-secondary btn-sm text-primary ml-0"
									>
										{t("add-schedule-request")}
									</Button>
								)}

							{namespace === "timesheet" && (
								<Button
									onClick={() => open("add-limited-pay-code")}
									className="border-primary px-3 btn-round mb-2 mb-md-2 btn btn-secondary btn-sm text-primary ml-0"
								>
									{t("add-pay-code-request")}
								</Button>
							)}
						</Col>
					</Row>
				</CardHeader>

				<Card className="mb-auto h-100">
					<Actions
						filters={table.getState().columnFilters}
						setFilters={table.setColumnFilters}
						columns={table.getAllColumns()}
					>
						<BulkActions
							rows={table.getState().rowSelection}
							selectedRows={
								table
									.getSelectedRowModel()
									.flatRows?.map((row) => row.original) || []
							}
							batchRemove={openDeleteModal}
							canDelete={(row) =>
								canRequest(accessMap, {
									namespace,
									type: row?.type,
									action: "canDelete",
								})
							}
						/>
					</Actions>

					<NTable
						table={table}
						hasChangedDateFilter={hasChanges}
						setHasChangedDateFilter={setHasChanges}
					/>
				</Card>

				{deleteModal}
			</Card>

			<Drawer
				open={isEditOpen}
				close={closeEdit}
				title={t("edit-request")}
				className="edit-request-drawer"
			>
				{editDrawerContent}
			</Drawer>

			<AddShift
				isOpen={visible && addRequestType === "add-shift"}
				onClose={close}
				setData={setData}
				setTotalItems={setTotalItems}
			/>

			<AddAbsence
				isOpen={visible && addRequestType === "add-absence"}
				onClose={close}
				setData={setData}
				setTotalItems={setTotalItems}
				setCanRefetchPlans={setCanRefetchPlans}
			/>

			<AddSchedule
				isOpen={visible && addRequestType === "add-schedule"}
				onClose={close}
				setData={setData}
				setTotalItems={setTotalItems}
			/>

			<AddPayCode
				isOpen={visible && addRequestType === "add-limited-pay-code"}
				onClose={close}
				onSuccess={onAddPayCodeSuccess}
			/>

			{cancelModal}
			{drawer}
			{warningModal}
		</div>
	);
}

export default MyRequest;
